import React, {FC, PropsWithChildren} from 'react';

import {findAncestorByTag, isElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {hasMetaKey} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {FrontMouseEvent} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {MouseButtonsEnum} from '../../../helpers/mouseHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {useLinkNavigation} from '../navigation/useLinkNavigation';

/*
 * Props.
 */

interface LinkTrapProps {
  className?: string;
  /** Allows links to be followed only with the meta key held down. */
  requireMetaKey?: boolean;
  onTrack?: (href: string) => void;
}

/*
 * Component.
 */

/** Will open any link from the children in a new window. */
export const LinkTrap: FC<PropsWithChildren<LinkTrapProps>> = (props) => {
  const {className, children} = props;
  const {navigateToLink, maybeRenderLinkError} = useLinkNavigation();

  const trap = (
    // These ESLint accessibility rules are not applicable, because we're not changing link semantics.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={className} onClick={(event) => handleClick(event, props, navigateToLink)}>
      {children}
    </div>
  );

  return (
    <>
      {trap}
      {maybeRenderLinkError()}
    </>
  );
};

function handleClick(
  event: FrontMouseEvent,
  props: LinkTrapProps,
  navigateToLink: (link: string, shiftKey?: boolean) => void,
) {
  // Older browsers (including Firefox) may send click events for non-primary buttons.
  if (event.button !== MouseButtonsEnum.PRIMARY) {
    return;
  }

  // Check the meta key, if needed.
  if (props.requireMetaKey && !hasMetaKey(event)) {
    return;
  }

  // We need to work with HTML elements past this point.
  const {target, currentTarget} = event;
  if (!isElement(target) || !isElement(currentTarget)) {
    return;
  }

  // Find the closest anchor.
  const anchor = findAncestorByTag(target, currentTarget, 'A');
  if (!anchor) {
    return;
  }

  const href = anchor.getAttribute('href');
  if (href) {
    maybeTrack(href);

    const {onTrack} = props;
    if (onTrack) {
      onTrack(href);
    }

    // Prevent navigation (in browser) or new-window event (in Electron)
    // and use our browser service instead.
    event.preventDefault();
    navigateToLink(href, event.shiftKey);
  }
}

function maybeTrack(href: string) {
  if (href.startsWith('https://www.google.com/calendar/event?action=RESPOND')) {
    trackInterfaceEvent(InterfaceEventsEnum.CLICK_GCAL_RSVP_LINK);
  }
}
